import PageLayout from '@/components/PageLayout';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import BrownianMotion from '@/components/BrownianMotion';
import StylizedDivider from '@/components/StylizedDivider';
import InfoRequestModal from '@/components/InfoRequestModal';
import { useEffect, useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

interface InfoRequestState {
  isOpen: boolean;
  topic: string;
  category: string;
}

const Research = () => {
  const isMobile = useIsMobile();
  const [pageVersion] = useState(`research-${Date.now()}`);
  const [infoRequest, setInfoRequest] = useState<InfoRequestState>({
    isOpen: false,
    topic: '',
    category: ''
  });

  const openInfoRequest = (topic: string, category: string) => {
    setInfoRequest({ isOpen: true, topic, category });
  };

  const closeInfoRequest = () => {
    setInfoRequest({ isOpen: false, topic: '', category: '' });
  };

  // Implement aggressive cache busting for Research page
  useEffect(() => {
    console.log(`Research page loaded with version ${pageVersion} - Mobile: ${isMobile ? 'Yes' : 'No'}`);

    // Generate unique timestamps for versioning
    const timestamp = Date.now();

    // Mark page as fresh
    document.documentElement.setAttribute('data-research-version', timestamp.toString());
    document.body.setAttribute('data-page', 'research');
    document.body.setAttribute('data-timestamp', timestamp.toString());

    // Force all section elements to use latest version
    document.querySelectorAll('section').forEach(section => {
      section.setAttribute('data-version', timestamp.toString());
      section.setAttribute('key', `section-${timestamp}-${Math.random().toString(36).substring(2, 9)}`);
    });

    // Force reload of all images
    document.querySelectorAll('img').forEach(img => {
      if (img.src && !img.src.includes('data:')) {
        const srcBase = img.src.split('?')[0];
        img.src = `${srcBase}?v=${timestamp}&t=${Math.random().toString(36).substring(2, 9)}`;
      }
    });

    // Add iOS and Safari specific fixes
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    if ((isIOS || isSafari) && isMobile) {
      console.log('iOS/Safari detected on Research page - applying fixes');

      // Force a slight re-rendering to bypass Safari cache
      document.body.style.opacity = '0.99';
      setTimeout(() => {
        document.body.style.opacity = '1';

        // Extra refresh of images for iOS Safari
        document.querySelectorAll('img').forEach(img => {
          if (img.src && !img.src.includes('data:')) {
            const currentSrc = img.src;
            img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'; // Blank gif
            setTimeout(() => {
              img.src = `${currentSrc}&r=${Math.random()}`;
            }, 50);
          }
        });
      }, 100);
    }
  }, [pageVersion, isMobile]);
  return <PageLayout>
      <main className="pt-20" key={`research-main-${pageVersion}`} data-version={pageVersion}>
        {/* Hero Section */}
        <section className="py-12 md:py-16 relative overflow-hidden grid-bg" key={`research-hero-${pageVersion}`}>
          <BrownianMotion particleCount={isMobile ? 2 : 3} speed={0.3} trailLength={20} key={`brownian-${pageVersion}`} />
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Research Initiatives</h1>
              
            </div>
          </div>
        </section>
        
        {/* Research Approach */}
        <section className="py-10 md:py-14 relative" key={`research-approach-${pageVersion}`}>
          <BrownianMotion particleCount={2} speed={0.25} trailLength={15} key={`brownian-approach-${pageVersion}`} />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto">
              
              <p className="text-lg text-delphi-light/70 mb-8">
                We believe that many groundbreaking scientific discoveries are overlooked due to the constraints of conventional funding models. DEL(PHI) addresses this gap by channeling trading profits into high-potential research areas that might otherwise go unexplored.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-8">
                <div className="data-section p-6">
                  <h3 className="text-xl font-bold mb-3">Unconventional Funding</h3>
                  <p className="text-delphi-light/70">
                    Unlike traditional research grants with restrictive parameters, our funding prioritizes bold theoretical explorations that challenge established paradigms.
                  </p>
                </div>
                
                <div className="data-section p-6">
                  <h3 className="text-xl font-bold mb-3">Cross-Disciplinary Focus</h3>
                  <p className="text-delphi-light/70">
                    We actively promote research that crosses traditional academic boundaries, fostering innovation at the intersection of fields.
                  </p>
                </div>
                
                <div className="data-section p-6">
                  <h3 className="text-xl font-bold mb-3">No Ownership Claims</h3>
                  <p className="text-delphi-light/70">
                    DEL(PHI) will never assume ownership or attempt to capitalize on any research conducted with our funding. We believe knowledge should remain with its creators and benefit humanity.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Stylized Divider */}
        <StylizedDivider />
        
        {/* Current Research Projects */}
        <section className="py-10 md:py-14 bg-delphi-blue/10 relative" key={`research-projects-${pageVersion}`}>
          <BrownianMotion particleCount={2} speed={0.25} trailLength={15} key={`brownian-projects-${pageVersion}`} />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">Current Research Projects</h2>
              
              <div className="space-y-6 md:space-y-10">
                {/* Project 1 */}
                <div className="relative bg-gradient-to-br from-delphi-red/10 via-transparent to-delphi-red/5 backdrop-blur-sm p-[2px] rounded-2xl shadow-[0_0_20px_rgba(234,56,76,0.15)]">
                  <div className="absolute inset-0 rounded-2xl border-2 border-delphi-red/40 pointer-events-none" />
                  <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-delphi-red/50 via-delphi-red/20 to-delphi-red/50 opacity-60 blur-[1px] pointer-events-none" />
                  <div className="relative bg-delphi-dark rounded-xl p-6 md:p-8">
                    <div className="mb-6">
                      <span className="inline-block bg-delphi-red/20 text-delphi-red text-xs font-medium rounded px-2.5 py-1">Theoretical Physics</span>
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-4">The Implicit Dynamics of Cumulative Measurement: Entropy and the Predictability of Observables</h3>
                    
                    <p className="text-delphi-light/70 mb-6">This paper explores the hypothesis that 'quantum' behavior is an emergent consequence of cumulative time-like observation, rather than an intrinsically strange interpretation of nature. Exploitable aspects of these emergent dynamics are established quantitatively for one observable in particular: a stock's price. A plethora of interesting analogous behaviors emerge as observables become quasiparticles in a probabilistic phase space. This is the paper that serves as the foundation of DEL(PHI)'s trading strategy.</p>
                    
                    <div className="mb-6">
                      <h4 className="text-lg font-medium mb-2">Key Findings</h4>
                      <ul className="list-disc pl-5 space-y-1 text-delphi-light/70">
                        <li>Established mathematical framework for transforming time-like observables into binary sequences, on which Operators act to create a phase space with learnable dynamics.</li>
                        <li>Defines a real-world analog to the Quantum Wavefunction, with interesting implications.</li>
                        <li>Outlines an exploitable pattern in the dynamics of these Observables, particularly in the case of high-volume equity prices.</li>
                      </ul>
                    </div>
                    
                    <div className="flex flex-wrap gap-4">
                      <button 
                        onClick={() => openInfoRequest('The Implicit Dynamics of Cumulative Measurement: Entropy and the Predictability of Observables', 'Theoretical Physics')}
                        className="inline-flex items-center px-4 py-2 bg-delphi-red/10 text-delphi-red rounded text-sm hover:bg-delphi-red/20 transition-colors"
                      >
                        <span className="mr-2">Request Further Information</span>
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Project 2 */}
                <div className="relative bg-gradient-to-br from-delphi-red/10 via-transparent to-delphi-red/5 backdrop-blur-sm p-[2px] rounded-2xl shadow-[0_0_20px_rgba(234,56,76,0.15)]">
                  <div className="absolute inset-0 rounded-2xl border-2 border-delphi-red/40 pointer-events-none" />
                  <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-delphi-red/50 via-delphi-red/20 to-delphi-red/50 opacity-60 blur-[1px] pointer-events-none" />
                  <div className="relative bg-delphi-dark rounded-xl p-6 md:p-8">
                    <div className="mb-6">
                      <span className="inline-block bg-delphi-red/20 text-delphi-red text-xs font-medium rounded px-2.5 py-1">Applied Mathematics</span>
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-4">Emergent Determinism in Binary Stochastic Processes: A Multiscale Analysis</h3>
                    
                    <p className="text-delphi-light/70 mb-6">
                      This research develops advanced mathematical tools for identifying emergent properties in complex adaptive systems, with applications ranging from market behavior to climate modeling and biological systems.
                    </p>
                    
                    <div className="mb-6">
                      <h4 className="text-lg font-medium mb-2">Key Findings</h4>
                      <ul className="list-disc pl-5 space-y-1 text-delphi-light/70">
                        <li>Created new methodologies for quantifying phase transitions in complex systems</li>
                        <li>Applied novel gradient field mathematics to financial market prediction</li>
                        <li>Developed cross-disciplinary applications for climate stability modeling</li>
                      </ul>
                    </div>
                    
                    <div className="flex flex-wrap gap-4">
                      <button 
                        onClick={() => openInfoRequest('Emergent Determinism in Binary Stochastic Processes: A Multiscale Analysis', 'Applied Mathematics')}
                        className="inline-flex items-center px-4 py-2 bg-delphi-red/10 text-delphi-red rounded text-sm hover:bg-delphi-red/20 transition-colors"
                      >
                        <span className="mr-2">Request Further Information</span>
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Project 3 */}
                <div className="relative bg-gradient-to-br from-delphi-red/10 via-transparent to-delphi-red/5 backdrop-blur-sm p-[2px] rounded-2xl shadow-[0_0_20px_rgba(234,56,76,0.15)]">
                  <div className="absolute inset-0 rounded-2xl border-2 border-delphi-red/40 pointer-events-none" />
                  <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-delphi-red/50 via-delphi-red/20 to-delphi-red/50 opacity-60 blur-[1px] pointer-events-none" />
                  <div className="relative bg-delphi-dark rounded-xl p-6 md:p-8">
                    <div className="mb-6">
                      <span className="inline-block bg-delphi-red/20 text-delphi-red text-xs font-medium rounded px-2.5 py-1">Market Theory</span>
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-4">Option Expiration Dates as Osmotic Membranes in the Holographic Dendogram: Entropic Forces on the Observable's Geodesic in Outcome Probability Space</h3>
                    
                    <p className="text-delphi-light/70 mb-6">This project builds on previous research into Entropic Forces, especially with regards to our framework's arrival at a similar conclusion. Particularly, a property of Entropic Forces that depends on multi-scale continuous observation is probed to investigate the effect (if any) of the Options Market's weekly expiration date.</p>
                    
                    <div className="mb-6">
                      <h4 className="text-lg font-medium mb-2">Key Findings</h4>
                      <ul className="list-disc pl-5 space-y-1 text-delphi-light/70">
                        <li>Identified predictable patterns in near-expiration option behavior</li>
                        <li>Developed mathematical model for volatility amplification in expiration windows</li>
                        <li>Created adaptive algorithm for optimal execution during high-volatility periods</li>
                      </ul>
                    </div>
                    
                    <div className="flex flex-wrap gap-4">
                      <button 
                        onClick={() => openInfoRequest('Option Expiration Dates as Osmotic Membranes in the Holographic Dendogram', 'Market Theory')}
                        className="inline-flex items-center px-4 py-2 bg-delphi-red/10 text-delphi-red rounded text-sm hover:bg-delphi-red/20 transition-colors"
                      >
                        <span className="mr-2">Request Further Information</span>
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Project 4 */}
                <div className="relative bg-gradient-to-br from-delphi-red/10 via-transparent to-delphi-red/5 backdrop-blur-sm p-[2px] rounded-2xl shadow-[0_0_20px_rgba(234,56,76,0.15)]">
                  <div className="absolute inset-0 rounded-2xl border-2 border-delphi-red/40 pointer-events-none" />
                  <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-delphi-red/50 via-delphi-red/20 to-delphi-red/50 opacity-60 blur-[1px] pointer-events-none" />
                  <div className="relative bg-delphi-dark rounded-xl p-6 md:p-8">
                    <div className="mb-6">
                      <span className="inline-block bg-delphi-red/20 text-delphi-red text-xs font-medium rounded px-2.5 py-1">Epigenetics & Machine Learning</span>
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-4">Beyond the Genome: Conserved Spatial Architecture as a Determinant of Epigenetic Regulation</h3>
                    
                    <p className="text-delphi-light/70 mb-6">This research explores the hypothesis that physical location within an organism plays a fundamental and conserved role in the establishment and maintenance of epigenetic states. We propose that relative spatial positioning among tissues and organs acts not merely as a consequence of differentiation, but as an instructive and stabilizing factor in cell-type specification. This spatially-informed view of cell fate determination challenges conventional assumptions of cell-autonomous identity and opens a new frontier for integrative studies in developmental systems biology.</p>
                    
                    <div className="mb-6">
                      <h4 className="text-lg font-medium mb-2">Key Findings</h4>
                      <ul className="list-disc pl-5 space-y-1 text-delphi-light/70">
                        <li>Identified conserved inter-organ spatial relationships across species that point to a shared architectural logic underlying epigenetic patterning</li>
                        <li>Developed a theoretical and computational framework for modeling how spatial constraints may act as inputs to epigenetic regulation</li>
                        <li>Formulated testable predictions for future experimental validation of spatial influences on epigenetic mechanisms</li>
                        <li>Demonstrated evidence from comparative anatomy, developmental biology, and spatial transcriptomics supporting the spatial architecture hypothesis</li>
                      </ul>
                    </div>
                    
                    <div className="flex flex-wrap gap-4">
                      <button 
                        onClick={() => openInfoRequest('Beyond the Genome: Conserved Spatial Architecture as a Determinant of Epigenetic Regulation', 'Epigenetics & Machine Learning')}
                        className="inline-flex items-center px-4 py-2 bg-delphi-red/10 text-delphi-red rounded text-sm hover:bg-delphi-red/20 transition-colors"
                      >
                        <span className="mr-2">Request Further Information</span>
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Project 5 - New Biomimicry Project */}
                <div className="relative bg-gradient-to-br from-delphi-red/10 via-transparent to-delphi-red/5 backdrop-blur-sm p-[2px] rounded-2xl shadow-[0_0_20px_rgba(234,56,76,0.15)]">
                  <div className="absolute inset-0 rounded-2xl border-2 border-delphi-red/40 pointer-events-none" />
                  <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-delphi-red/50 via-delphi-red/20 to-delphi-red/50 opacity-60 blur-[1px] pointer-events-none" />
                  <div className="relative bg-delphi-dark rounded-xl p-6 md:p-8">
                    <div className="mb-6">
                      <span className="inline-block bg-delphi-red/20 text-delphi-red text-xs font-medium rounded px-2.5 py-1">Neuroscience & AI</span>
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-4">Biomimicry of Dragonfly Optical Lobe for Spatiotemporal Trajectory Prediction with Deep Neural Networks</h3>
                    
                    <p className="text-delphi-light/70 mb-6">This research explores the innovative application of dragonfly neural architectures to enhance trajectory prediction in artificial neural networks. By studying the unique structure and function of dragonfly optical lobes, we aim to develop more efficient and accurate methods for predicting object trajectories in complex environments.</p>
                    
                    <div className="mb-6">
                      <h4 className="text-lg font-medium mb-2">Key Findings</h4>
                      <ul className="list-disc pl-5 space-y-2 text-delphi-light/70">
                        <li>Mapped the neural architecture of dragonfly optical lobes and identified key computational principles</li>
                        <li>Developed novel neural network architectures inspired by dragonfly visual processing systems</li>
                        <li>Demonstrated improved performance in real-time trajectory prediction compared to traditional deep learning approaches</li>
                        <li>Created biomimetic algorithms that require significantly less computational resources while maintaining accuracy</li>
                      </ul>
                    </div>
                    
                    <div className="flex flex-wrap gap-4">
                      <button 
                        onClick={() => openInfoRequest('Biomimicry of Dragonfly Optical Lobe for Spatiotemporal Trajectory Prediction', 'Neuroscience & AI')}
                        className="inline-flex items-center px-4 py-2 bg-delphi-red/10 text-delphi-red rounded text-sm hover:bg-delphi-red/20 transition-colors"
                      >
                        <span className="mr-2">Request Further Information</span>
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Stylized Divider */}
        <StylizedDivider />
        
        {/* Research Collaboration CTA */}
        <section className="py-12 md:py-16 relative" key={`research-cta-${pageVersion}`}>
          <BrownianMotion particleCount={3} speed={0.35} trailLength={18} color="#ea384c" key={`brownian-cta-${pageVersion}`} />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">Research Collaboration</h2>
              <p className="text-lg text-delphi-light/70 mb-10">
                DEL(PHI) is open to strategic research partnerships with academic institutions and individual researchers whose work aligns with our unconventional approach to scientific discovery.
              </p>
              
              <Link to="/contact" className="px-6 py-3 bg-delphi-red text-white rounded-md font-medium inline-flex items-center gap-2 hover:bg-red-600 transition-colors">
                <span>Discuss Collaboration Opportunities</span>
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      </main>
      
      <InfoRequestModal
        isOpen={infoRequest.isOpen}
        onClose={closeInfoRequest}
        topic={infoRequest.topic}
        category={infoRequest.category}
      />
    </PageLayout>;
};
export default Research;